/**
 * FormPropertiesPanel — right properties inspector for AdvancedFormBuilder.
 * Shows element content/design/advanced tabs, section styles, or global styles.
 * Extracted from AdvancedFormBuilder.js (Phase 3 split).
 */
import React from 'react';
import { useFormBuilder } from './FormBuilderContext';

export function FormPropertiesPanel() {
  const {
    propertiesCollapsed, setPropertiesCollapsed,
    propertiesTab, setPropertiesTab,
    selectedElementData, isSelectedElementGlobal, currentSection,
    globalStyles, setGlobalStyles,
    backgroundImageInputRef,
    updateElement, updateElementAlignment,
    handleImageUpload, handleVideoUpload,
    handleBackgroundImageUpload, removeBackgroundImage,
    renderDesignProperties, renderSectionProperties,
  } = useFormBuilder();

  return (
    <div className={`properties-panel ${propertiesCollapsed ? 'collapsed' : ''}`}>
      <div className="properties-header">
        <button className="collapse-properties" onClick={() => setPropertiesCollapsed(!propertiesCollapsed)}>
          {propertiesCollapsed ? '←' : '→'}
        </button>
        <h3>Properties</h3>
      </div>

      {!propertiesCollapsed && (
        <div className="properties-content">
          {selectedElementData ? (
            <div className="properties-tabs">
              <div className="properties-tab-buttons">
                <button className={`properties-tab-button ${propertiesTab === 'content' ? 'active' : ''}`} onClick={() => setPropertiesTab('content')}>
                  📝 Content
                </button>
                <button className={`properties-tab-button ${propertiesTab === 'design' ? 'active' : ''}`} onClick={() => setPropertiesTab('design')}>
                  🎨 Design
                </button>
                <button className={`properties-tab-button ${propertiesTab === 'advanced' ? 'active' : ''}`} onClick={() => setPropertiesTab('advanced')}>
                  ⚙️ Advanced
                </button>
              </div>

              <div className="properties-tab-content">
                {propertiesTab === 'content' && (
                  <div className="properties-group">
                    <h4>Content Properties {isSelectedElementGlobal && <span className="global-badge">🌍 Global</span>}</h4>
                    <div className="property-group">
                      <label>Label:</label>
                      <input type="text" value={selectedElementData.label}
                        onChange={(e) => updateElement(selectedElementData.id, { label: e.target.value }, isSelectedElementGlobal)} />
                    </div>

                    {['header','image','video','map','table','link','container','button','submit'].includes(selectedElementData.type) && (
                      <div className="property-group">
                        <label>Content Alignment:</label>
                        <div className="alignment-controls">
                          {['left','center','right'].map(align => (
                            <button key={align} className={`align-btn ${selectedElementData.alignment === align ? 'active' : ''}`}
                              onClick={() => updateElementAlignment(selectedElementData.id, align, align, isSelectedElementGlobal)}
                              title={`Align ${align}`}>
                              {align === 'left' ? '⬅️' : align === 'center' ? '⬜' : '➡️'}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedElementData.type === 'image' && (
                      <>
                        <div className="property-group">
                          <label>Alt Text:</label>
                          <input type="text" value={selectedElementData.altText}
                            onChange={(e) => updateElement(selectedElementData.id, { altText: e.target.value }, isSelectedElementGlobal)} />
                        </div>
                        <div className="property-group">
                          <label>Upload Image:</label>
                          <input type="file" accept="image/*"
                            onChange={(e) => { const f = e.target.files[0]; if (f) handleImageUpload(selectedElementData.id, f, isSelectedElementGlobal); }} />
                        </div>
                        {selectedElementData.imageUrl && (
                          <div className="property-group">
                            <label>Current Image:</label>
                            <img src={selectedElementData.imageUrl} alt="Preview" style={{ maxWidth: '100%', maxHeight: '150px', marginTop: '10px' }} />
                          </div>
                        )}
                      </>
                    )}

                    {selectedElementData.type === 'video' && (
                      <>
                        <div className="property-group">
                          <label>Upload Video:</label>
                          <input type="file" accept="video/*"
                            onChange={(e) => { const f = e.target.files[0]; if (f) handleVideoUpload(selectedElementData.id, f, isSelectedElementGlobal); }} />
                        </div>
                        {selectedElementData.videoUrl && (
                          <div className="property-group">
                            <label>Current Video:</label>
                            <video src={selectedElementData.videoUrl} controls style={{ maxWidth: '100%', maxHeight: '150px', marginTop: '10px' }} />
                          </div>
                        )}
                      </>
                    )}

                    {selectedElementData.type === 'map' && (
                      <>
                        <div className="property-group">
                          <label>Latitude:</label>
                          <input type="text" value={selectedElementData.lat}
                            onChange={(e) => updateElement(selectedElementData.id, { lat: e.target.value }, isSelectedElementGlobal)} />
                        </div>
                        <div className="property-group">
                          <label>Longitude:</label>
                          <input type="text" value={selectedElementData.lng}
                            onChange={(e) => updateElement(selectedElementData.id, { lng: e.target.value }, isSelectedElementGlobal)} />
                        </div>
                        <div className="property-group">
                          <label>Zoom Level:</label>
                          <input type="number" value={selectedElementData.zoom}
                            onChange={(e) => updateElement(selectedElementData.id, { zoom: parseInt(e.target.value) }, isSelectedElementGlobal)} />
                        </div>
                      </>
                    )}

                    {selectedElementData.type === 'table' && (
                      <>
                        <div className="property-group">
                          <label>Rows:</label>
                          <input type="number" value={selectedElementData.rows}
                            onChange={(e) => {
                              const rows = parseInt(e.target.value);
                              const newContent = Array(rows).fill().map(() => Array(selectedElementData.columns).fill('Cell'));
                              updateElement(selectedElementData.id, { rows, content: newContent }, isSelectedElementGlobal);
                            }} />
                        </div>
                        <div className="property-group">
                          <label>Columns:</label>
                          <input type="number" value={selectedElementData.columns}
                            onChange={(e) => {
                              const columns = parseInt(e.target.value);
                              const newContent = Array(selectedElementData.rows).fill().map(() => Array(columns).fill('Cell'));
                              updateElement(selectedElementData.id, { columns, content: newContent }, isSelectedElementGlobal);
                            }} />
                        </div>
                      </>
                    )}

                    {selectedElementData.type === 'link' && (
                      <>
                        <div className="property-group">
                          <label>Link URL:</label>
                          <input type="text" value={selectedElementData.href}
                            onChange={(e) => updateElement(selectedElementData.id, { href: e.target.value }, isSelectedElementGlobal)} />
                        </div>
                        <div className="property-group">
                          <label>Link Text:</label>
                          <input type="text" value={selectedElementData.text}
                            onChange={(e) => updateElement(selectedElementData.id, { text: e.target.value }, isSelectedElementGlobal)} />
                        </div>
                      </>
                    )}

                    {!['header','divider','image','video','map','table','link','container'].includes(selectedElementData.type) && (
                      <>
                        <div className="property-group">
                          <label>Placeholder:</label>
                          <input type="text" value={selectedElementData.placeholder}
                            onChange={(e) => updateElement(selectedElementData.id, { placeholder: e.target.value }, isSelectedElementGlobal)} />
                        </div>
                        <div className="property-group">
                          <label>Description:</label>
                          <textarea value={selectedElementData.description}
                            onChange={(e) => updateElement(selectedElementData.id, { description: e.target.value }, isSelectedElementGlobal)}
                            rows="3" />
                        </div>
                        <div className="property-group">
                          <label>
                            <input type="checkbox" checked={selectedElementData.required}
                              onChange={(e) => updateElement(selectedElementData.id, { required: e.target.checked }, isSelectedElementGlobal)} />
                            Required Field
                          </label>
                        </div>
                      </>
                    )}

                    {['select','radio'].includes(selectedElementData.type) && (
                      <div className="property-group">
                        <label>Options (comma separated):</label>
                        <input type="text" value={selectedElementData.options.join(', ')}
                          onChange={(e) => updateElement(selectedElementData.id, { options: e.target.value.split(',').map(o => o.trim()) }, isSelectedElementGlobal)} />
                      </div>
                    )}

                    {selectedElementData.type === 'header' && (
                      <div className="property-group">
                        <label>Header Level:</label>
                        <select value={selectedElementData.headerLevel}
                          onChange={(e) => updateElement(selectedElementData.id, { headerLevel: e.target.value }, isSelectedElementGlobal)}>
                          <option value="h1">H1</option>
                          <option value="h2">H2</option>
                          <option value="h3">H3</option>
                          <option value="h4">H4</option>
                        </select>
                      </div>
                    )}

                    {['button','submit'].includes(selectedElementData.type) && (
                      <div className="property-group">
                        <label>Button Text:</label>
                        <input type="text" value={selectedElementData.buttonText}
                          onChange={(e) => updateElement(selectedElementData.id, { buttonText: e.target.value }, isSelectedElementGlobal)} />
                      </div>
                    )}
                  </div>
                )}

                {propertiesTab === 'design' && renderDesignProperties(selectedElementData, isSelectedElementGlobal)}

                {propertiesTab === 'advanced' && (
                  <div className="properties-group">
                    <h4>Advanced Properties</h4>
                    <div className="property-group">
                      <label>Element ID:</label>
                      <input type="text" value={selectedElementData.id} disabled />
                    </div>
                    <div className="property-group">
                      <label>Custom Classes:</label>
                      <input type="text" value={selectedElementData.customClasses || ''} placeholder="class1 class2"
                        onChange={(e) => updateElement(selectedElementData.id, { customClasses: e.target.value }, isSelectedElementGlobal)} />
                    </div>
                    <div className="property-group">
                      <label>Validation Pattern:</label>
                      <input type="text" value={selectedElementData.validation?.pattern || ''} placeholder="[A-Za-z]{3,}"
                        onChange={(e) => updateElement(selectedElementData.id, { validation: { ...selectedElementData.validation, pattern: e.target.value } }, isSelectedElementGlobal)} />
                    </div>
                    <div className="property-group">
                      <label>Error Message:</label>
                      <input type="text" value={selectedElementData.validation?.errorMessage || ''} placeholder="Please enter a valid value"
                        onChange={(e) => updateElement(selectedElementData.id, { validation: { ...selectedElementData.validation, errorMessage: e.target.value } }, isSelectedElementGlobal)} />
                    </div>
                  </div>
                )}
              </div>
            </div>

          ) : currentSection ? (
            renderSectionProperties()

          ) : (
            /* Global Styles panel */
            <div className="properties-group">
              <h4>🌍 Global Styles</h4>

              <div className="property-group">
                <label>Background Color:</label>
                <div className="background-color-controls">
                  <input type="color" value={globalStyles.backgroundColor || '#ffffff'}
                    onChange={(e) => setGlobalStyles({ ...globalStyles, backgroundColor: e.target.value })} />
                  <button className="no-color-btn" onClick={() => setGlobalStyles({ ...globalStyles, backgroundColor: 'transparent' })} title="No Color">
                    🚫 No Color
                  </button>
                </div>
              </div>

              <div className="property-group">
                <label>Background Image:</label>
                <div className="background-image-controls">
                  <div className="background-upload-controls">
                    <button className="upload-background-btn" onClick={() => backgroundImageInputRef.current?.click()}>
                      📁 Upload Background
                    </button>
                    {globalStyles.backgroundImage && (
                      <button className="remove-background-btn" onClick={() => removeBackgroundImage('global')}>🗑️ Remove</button>
                    )}
                  </div>
                  <input ref={backgroundImageInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={(e) => handleBackgroundImageUpload(e, 'global')} />
                  {globalStyles.backgroundImage && (
                    <div className="background-preview">
                      <div className="preview-image" style={{ backgroundImage: globalStyles.backgroundImage, backgroundSize: 'cover', backgroundPosition: 'center', height: '80px', borderRadius: '4px', marginTop: '8px' }} />
                    </div>
                  )}
                </div>
              </div>

              {globalStyles.backgroundImage && (
                <div className="property-row">
                  <div className="property-group">
                    <label>Background Size:</label>
                    <select value={globalStyles.backgroundSize || 'cover'} onChange={(e) => setGlobalStyles({ ...globalStyles, backgroundSize: e.target.value })}>
                      <option value="cover">Cover</option>
                      <option value="contain">Contain</option>
                      <option value="auto">Auto</option>
                      <option value="100% 100%">100% 100%</option>
                    </select>
                  </div>
                  <div className="property-group">
                    <label>Background Position:</label>
                    <select value={globalStyles.backgroundPosition || 'center'} onChange={(e) => setGlobalStyles({ ...globalStyles, backgroundPosition: e.target.value })}>
                      {['center','top','bottom','left','right'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                    </select>
                  </div>
                </div>
              )}

              <div className="property-group">
                <label>Text Color:</label>
                <input type="color" value={globalStyles.color || '#000000'} onChange={(e) => setGlobalStyles({ ...globalStyles, color: e.target.value })} />
              </div>

              <div className="property-row">
                <div className="property-group">
                  <label>Font Family:</label>
                  <select value={globalStyles.fontFamily || "'Inter', sans-serif"} onChange={(e) => setGlobalStyles({ ...globalStyles, fontFamily: e.target.value })}>
                    <option value="'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif">Inter</option>
                    <option value="Arial, sans-serif">Arial</option>
                    <option value="'Helvetica Neue', Helvetica, sans-serif">Helvetica</option>
                    <option value="'Times New Roman', serif">Times New Roman</option>
                    <option value="'Georgia', serif">Georgia</option>
                  </select>
                </div>
                <div className="property-group">
                  <label>Font Size:</label>
                  <input type="text" value={globalStyles.fontSize || '16px'} placeholder="16px" onChange={(e) => setGlobalStyles({ ...globalStyles, fontSize: e.target.value })} />
                </div>
              </div>

              <div className="property-group">
                <label>Line Height:</label>
                <input type="text" value={globalStyles.lineHeight || '1.5'} placeholder="1.5" onChange={(e) => setGlobalStyles({ ...globalStyles, lineHeight: e.target.value })} />
              </div>

              <h5>📐 Layout & Spacing</h5>
              <div className="property-row">
                <div className="property-group">
                  <label>Width:</label>
                  <input type="text" value={globalStyles.width || '100%'} placeholder="100%" onChange={(e) => setGlobalStyles({ ...globalStyles, width: e.target.value })} />
                </div>
                <div className="property-group">
                  <label>Height:</label>
                  <input type="text" value={globalStyles.height || 'auto'} placeholder="auto" onChange={(e) => setGlobalStyles({ ...globalStyles, height: e.target.value })} />
                </div>
              </div>

              <div className="property-row">
                <div className="property-group">
                  <label>Max Width:</label>
                  <input type="text" value={globalStyles.maxWidth || '100%'} placeholder="1200px" onChange={(e) => setGlobalStyles({ ...globalStyles, maxWidth: e.target.value })} />
                </div>
                <div className="property-group">
                  <label>Min Height:</label>
                  <input type="text" value={globalStyles.minHeight || 'auto'} placeholder="400px" onChange={(e) => setGlobalStyles({ ...globalStyles, minHeight: e.target.value })} />
                </div>
              </div>

              <div className="property-row">
                <div className="property-group">
                  <label>Padding:</label>
                  <input type="text" value={globalStyles.padding || '20px'} placeholder="20px" onChange={(e) => setGlobalStyles({ ...globalStyles, padding: e.target.value })} />
                </div>
                <div className="property-group">
                  <label>Margin:</label>
                  <input type="text" value={globalStyles.margin || '0 auto'} placeholder="0 auto" onChange={(e) => setGlobalStyles({ ...globalStyles, margin: e.target.value })} />
                </div>
              </div>

              <div className="property-row">
                <div className="property-group">
                  <label>Border:</label>
                  <input type="text" value={globalStyles.border || 'none'} placeholder="1px solid #e2e8f0" onChange={(e) => setGlobalStyles({ ...globalStyles, border: e.target.value })} />
                </div>
                <div className="property-group">
                  <label>Border Radius:</label>
                  <input type="text" value={globalStyles.borderRadius || '0px'} placeholder="8px" onChange={(e) => setGlobalStyles({ ...globalStyles, borderRadius: e.target.value })} />
                </div>
              </div>

              <div className="property-group">
                <label>Box Shadow:</label>
                <input type="text" value={globalStyles.boxShadow || 'none'} placeholder="0 2px 8px rgba(0,0,0,0.1)" onChange={(e) => setGlobalStyles({ ...globalStyles, boxShadow: e.target.value })} />
              </div>

              <div className="property-group">
                <label>Opacity:</label>
                <input type="range" min="0" max="1" step="0.1" value={globalStyles.opacity ?? 1}
                  onChange={(e) => setGlobalStyles({ ...globalStyles, opacity: parseFloat(e.target.value) })} />
                <span className="range-value">{((globalStyles.opacity ?? 1) * 100)}%</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
